// src/components/TripNotificationBanner.jsx
import React from "react";
import { formatDateForUI } from "./TripList"; // existing helper
import { toInclusiveMs } from "../utils/date";

/**
 * TripNotificationBanner
 *
 * Props:
 *  - selectedTrip: object (trip currently loaded)
 *  - user: firebase user object (for ownership checks)
 *  - isOwner: boolean (precomputed by caller)
 *  - onStop: function -> called when user clicks "Stop trip now"
 *  - onExtend: function -> called when user clicks "Extend 24h"
 *
 * Behaviour implemented:
 * 1) Banner visible only to owner
 * 2) Banner visible only for the active trip (started === true) when expiration handling is needed
 * 3) If today is within [startDate, endDate] AND started === false -> show "scheduled but not started" (owner only)
 * 4) If started === true AND endDate < now -> show "expired" banner with Stop and Extend 24h buttons (owner only)
 */

function parseDateToMsInclusive(input) {
    try {
        const ms = toInclusiveMs(input);
        return Number.isFinite(ms) ? ms : NaN;
    } catch (e) {
        return NaN;
    }
}

export default function TripNotificationBanner({
    selectedTrip,
    user,
    isOwner = false,
    onStop,
    onExtend,
}) {
    if (!selectedTrip) return null;

    // owner detection (fall back to selectedTrip.ownerId or selectedTrip.owner_id)
    const isOwnerLocal =
        isOwner ||
        (user &&
            (selectedTrip.ownerId === user.uid || selectedTrip.owner_id === user.uid));

    if (!isOwnerLocal) {
        // banner is owner-only per requirement
        return null;
    }

    const now = Date.now();

    // parse start/end dates to inclusive milliseconds using utility
    const startMs = parseDateToMsInclusive(selectedTrip.startDate);
    const endMs = parseDateToMsInclusive(selectedTrip.endDate);

    const started = !!selectedTrip.started;

    // 3) scheduled for today but not started (owner only)
    // show only when started === false AND today is within [startDate, endDate] inclusive
    if (!started && Number.isFinite(startMs) && Number.isFinite(endMs) && now >= startMs && now <= endMs) {
        // If you want to show start/end labels, format them
        const startLabel = formatDateForUI(startMs);
        const endLabel = formatDateForUI(endMs);

        return (
            <div className="warning-banner" style={{ padding: 12 }}>
                ⚠️ Your trip "{selectedTrip.title}" is scheduled for today ({startLabel} → {endLabel}) but hasn’t been started yet.
                <div style={{ fontSize: 13, color: "#eee", marginTop: 6 }}>
                    Start the trip from your mobile app when you're ready so tracking can begin.
                </div>
            </div>
        );
    }

    // 4) expired trip while still started: owner must stop or extend
    // show when started === true and endDate < now
    if (started && Number.isFinite(endMs) && now > endMs) {
        const endLabel = formatDateForUI(endMs);

        return (
            <div
                className="warning-banner"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                }}
            >
                <div>
                    ⚠️ Your trip "{selectedTrip.title}" expired on {endLabel} but tracking is still active.
                    <div style={{ fontSize: 13, color: "#eee", marginTop: 6 }}>
                        Stop the trip now or extend the end date by 24 hours.
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className="btn-danger"
                        onClick={() => {
                            try {
                                onStop && onStop();
                            } catch (e) {
                                // caller should handle errors
                            }
                        }}
                        title="Stop trip now"
                    >
                        Stop trip now
                    </button>

                    <button
                        className="btn-secondary"
                        onClick={() => {
                            try {
                                // caller should implement the actual extension logic (e.g. update endDate to +1 day)
                                onExtend && onExtend();
                            } catch (e) {
                                // ignore; caller handles
                            }
                        }}
                        title="Extend end date by 24 hours"
                    >
                        Extend 24h
                    </button>
                </div>
            </div>
        );
    }

    // No banner needed for:
    // - non-owner (already returned)
    // - trips not started and not scheduled for today
    // - trips started and still within end date
    return null;
}
